import type { ColumnDef } from '@tanstack/react-table';
import { columnVisibilityFeature, rowSortingFeature, tableFeatures } from '@tanstack/react-table';
import type { InterviewInviteListItemDto } from '@sara/api-client';
import { Button } from '@sara/ui/components/button';
import { ArrowUp, ArrowDown, ChevronsUpDown, Link as LinkIcon, Check } from 'lucide-react';
import { useCopyToClipboard } from '../../../../hooks/useCopyToClipboard';
import { InterviewStatusCell } from '../InterviewStatusCell';

/**
 * The table-v9 feature registry for this table. Declared at module scope (the
 * table only reads its type, so a per-render object would be wasted work).
 * Sorting here is fully manual/server-side (no `sortedRowModel`), but
 * `rowSortingFeature` is still required for `state.sorting`,
 * `onSortingChange`, and `column.getIsSorted()`/`toggleSorting()`.
 */
export const features = tableFeatures({
  rowSortingFeature,
  // `row.getVisibleCells()`
  columnVisibilityFeature,
});

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') return <ArrowUp className="ml-1 inline h-3.5 w-3.5" strokeWidth={1.6} />;
  if (sorted === 'desc') return <ArrowDown className="ml-1 inline h-3.5 w-3.5" strokeWidth={1.6} />;
  return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-50" strokeWidth={1.6} />;
}

function SortHeader({
  children,
  sorted,
  onClick,
}: {
  children: React.ReactNode;
  sorted: false | 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <button
      className="inline-flex h-full items-center text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground/80 transition-colors hover:text-foreground"
      onClick={onClick}
    >
      {children}
      <SortIcon sorted={sorted} />
    </button>
  );
}

function CopyLinkCell({ url }: { url: string }) {
  const { copied, copy } = useCopyToClipboard();
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8 text-muted-foreground hover:text-foreground"
      aria-label="Copy session link"
      onClick={(e) => {
        e.stopPropagation();
        void copy(url);
      }}
    >
      {copied ? (
        <Check className="h-4 w-4 text-success" strokeWidth={1.6} />
      ) : (
        <LinkIcon className="h-4 w-4" strokeWidth={1.6} />
      )}
    </Button>
  );
}

function InitialsAvatar({ first, last }: { first: string; last: string }) {
  const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '·';
  return (
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-[11px] font-medium text-secondary-foreground">
      {initials}
    </span>
  );
}

export const interviewColumns: ColumnDef<typeof features, InterviewInviteListItemDto>[] = [
  {
    id: 'candidate',
    header: 'Candidate',
    enableSorting: false,
    cell: ({ row }) => {
      const { firstName, lastName, email } = row.original.candidate;
      return (
        <div className="flex items-center gap-3">
          <InitialsAvatar first={firstName} last={lastName} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-medium tracking-tight text-foreground">
              {firstName} {lastName}
            </p>
            <p className="truncate text-[12.5px] text-muted-foreground">{email}</p>
          </div>
        </div>
      );
    },
  },
  {
    id: 'interviewType',
    header: 'Template',
    enableSorting: false,
    cell: ({ row }) => (
      <span className="text-[13.5px] text-foreground">{row.original.interviewType.name}</span>
    ),
  },
  {
    id: 'externalIdentifiers',
    enableSorting: false,
    header: 'External identifiers',
    cell: ({ row }) => {
      const { externalUserId, externalSource } = row.original;
      if (!externalUserId && !externalSource) {
        return <span className="text-[13.5px] text-muted-foreground">—</span>;
      }
      return (
        <div className="min-w-0">
          {externalSource && (
            <p className="truncate text-[12px] text-muted-foreground">{externalSource}</p>
          )}
          {externalUserId && (
            <p className="truncate font-mono text-[11.5px] text-foreground">{externalUserId}</p>
          )}
        </div>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    enableSorting: false,
    cell: ({ row }) => {
      const { status, attempts, maxAttempts, endedBy } = row.original;
      return (
        <InterviewStatusCell
          status={status}
          attempts={attempts}
          maxAttempts={maxAttempts}
          endedBy={endedBy}
        />
      );
    },
  },
  {
    accessorKey: 'createdAt',
    id: 'createdAt',
    enableSorting: true,
    header: ({ column }) => (
      <SortHeader sorted={column.getIsSorted()} onClick={() => column.toggleSorting()}>
        Created
      </SortHeader>
    ),
    cell: ({ row }) => (
      <span className="text-[13px] tabular-nums text-muted-foreground">
        {new Date(row.original.createdAt).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })}
      </span>
    ),
  },
  {
    accessorKey: 'expiresAt',
    id: 'expiresAt',
    enableSorting: true,
    header: ({ column }) => (
      <SortHeader sorted={column.getIsSorted()} onClick={() => column.toggleSorting()}>
        Expires
      </SortHeader>
    ),
    cell: ({ row }) => (
      <span className="text-[13px] tabular-nums text-muted-foreground">
        {row.original.expiresAt
          ? new Date(row.original.expiresAt).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })
          : '—'}
      </span>
    ),
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    cell: ({ row }) => (
      <div className="flex justify-end">
        <CopyLinkCell url={row.original.url} />
      </div>
    ),
  },
];
